import IconButton from 'components/@extended/IconButton';
import { useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { CreateDialogActionBar } from 'components/create-dialog/CreateDialogFooter';
import { DraftChip } from 'components/drafts/DraftChip';
import {
    ArrowBack as ArrowBackIcon,
    CloseFullscreen as CloseFullscreenIcon,
    OpenInFull as OpenInFullIcon,
} from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import PageTitle from 'components/PageTitle';
import { Tooltip } from 'components/ui/tooltip';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'store';
import { toggleFullscreenMode } from 'store/reducers/menu';
import type { DraftDetail } from 'types/drafts';

type Props = {
    title: string | undefined;
    titleSuffixComponent?: React.ReactNode;
    actions: React.ReactNode;
    onBack?: () => void;
    /** The draft a create form edits: its name is the title, with the Draft chip and the day it is deleted. */
    draft?: DraftDetail;
};

const ICON_BUTTON_CLASS = 'text-fg-secondary hover:text-fg size-[30px]';

/**
 * A form's title bar with its actions. In the create dialog the dialog's header carries the title, so only the
 * actions and the title suffix are drawn, in the dialog's footer and header; the page's title, breadcrumb and
 * recently-viewed entry stay those of the page behind. The dialog draws a draft's title, chip and deletion day
 * itself.
 */
const ActionHeader = (props: Props): JSX.Element => {
    const inCreateDialog = useInCreateDialog();

    return inCreateDialog ? (
        <CreateDialogActionBar actions={props.actions} titleSuffix={props.titleSuffixComponent} />
    ) : (
        <PageActionHeader {...props} />
    );
};

function PageActionHeader({ title: formTitle, titleSuffixComponent, actions, onBack, draft }: Props): JSX.Element {
    const title = draft?.name ?? formTitle;
    const dispatch = useDispatch();
    const intl = useIntl();
    const { focusedEditMode, fullscreenMode } = useSelector((state) => state.menu);

    const handleToggleFullscreen = () => {
        dispatch(toggleFullscreenMode());
    };
    const fullscreenLabel = intl.formatMessage({
        id: fullscreenMode ? 'form.exit-fullscreen' : 'form.enter-fullscreen',
    });

    return (
        <>
            <PageTitle title={title} showBreadcrumb breadcrumbContent={title} />
            {/* `z-1` lifts the sticky bar over the form body it scrolls above, inside the body's own
                isolated stacking context. */}
            <div className="border-divider bg-grey-0 sticky top-0 z-1 flex h-18 items-center justify-between border-b px-6 py-2">
                <div className="flex items-center gap-4">
                    {onBack && (
                        <IconButton
                            onClick={onBack}
                            size="small"
                            className={`${ICON_BUTTON_CLASS} -mr-1`}
                            aria-label={intl.formatMessage({ id: 'back' })}
                        >
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    )}
                    {draft ? (
                        <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                                <h5 className="text-h5 m-0">{title}</h5>
                                <DraftChip />
                                {titleSuffixComponent ?? null}
                            </div>
                            <span className="text-caption text-grey-500">
                                <FormattedMessage
                                    id="draft.expires-on"
                                    values={{ date: <FormattedDate key="date" value={draft.deletesOn} /> }}
                                />
                            </span>
                        </div>
                    ) : (
                        <>
                            {title && <h5 className="text-h5 m-0">{title}</h5>}
                            {titleSuffixComponent ?? null}
                        </>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {focusedEditMode && (
                        <Tooltip content={fullscreenLabel}>
                            <IconButton
                                onClick={handleToggleFullscreen}
                                size="small"
                                aria-label={fullscreenLabel}
                                className={ICON_BUTTON_CLASS}
                            >
                                {fullscreenMode ? (
                                    <CloseFullscreenIcon fontSize="small" />
                                ) : (
                                    <OpenInFullIcon fontSize="small" />
                                )}
                            </IconButton>
                        </Tooltip>
                    )}
                    {actions}
                </div>
            </div>
        </>
    );
}

export default ActionHeader;
